import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Save, Send, Plus, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { freelanceGigsApi } from '../services/freelanceGigsApi';
import { splitMajorCities } from '../constants/cities';
import './FreelanceGigs.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface CVTemplateOption {
  id: number;
  category: string;
  value: string;
}

/**
 * Form to create or edit a Freelance Gig.
 *
 * Fields (mirrors the screenshot the user shared):
 *   - title          (required)
 *   - description    (required, ≥30 chars)
 *   - work_fields    (required multi-select from cv_templates.work_field)
 *   - city           (required) + nationwide radio
 *   - budget_text    (optional)
 *   - deadline_text  (optional)
 *
 * "Save as draft" → POST /freelance-gigs (status=draft)
 * "Publish"       → POST then PATCH /publish (which fires the email blast)
 *
 * If `:id` URL param is present, we hydrate from the existing gig (edit mode).
 */
const FreelanceGigCreate: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { id: editingId } = useParams<{ id: string }>();

  const [workFieldOptions, setWorkFieldOptions] = useState<CVTemplateOption[]>([]);
  const [cityOptions, setCityOptions] = useState<CVTemplateOption[]>([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [workFields, setWorkFields] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [nationwide, setNationwide] = useState(false);
  const [budgetText, setBudgetText] = useState('');
  const [deadlineText, setDeadlineText] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);
  const [showWorkFieldsPicker, setShowWorkFieldsPicker] = useState(false);
  const [workFieldSearch, setWorkFieldSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/cv-templates?grouped=true`);
        const data = res.data?.data || {};
        if (Array.isArray(data.work_field)) setWorkFieldOptions(data.work_field);
        if (Array.isArray(data.city)) setCityOptions(data.city);
      } catch (err) {
        console.error('Failed to load CV templates:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!editingId) return;
    (async () => {
      try {
        const { gig } = await freelanceGigsApi.getById(parseInt(editingId, 10));
        setTitle(gig.title);
        setDescription(gig.description);
        setWorkFields(gig.work_fields);
        setCity(gig.city ?? '');
        setNationwide(gig.nationwide);
        setBudgetText(gig.budget_text ?? '');
        setDeadlineText(gig.deadline_text ?? '');
      } catch (err: any) {
        setTopError(err?.message || 'Nepavyko įkrauti Gig');
      }
    })();
  }, [editingId]);

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login?type=employer&mode=employer" replace />;
  if (user?.user_type !== 'employer') return <Navigate to="/" replace />;

  const validateLocally = (): boolean => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = 'Pavadinimas yra privalomas';
    if (description.trim().length < 30) next.description = 'Aprašymas per trumpas (min 30 simbolių)';
    if (workFields.length === 0) next.work_fields = 'Pasirinkite bent vieną darbo sritį';
    if (!city.trim()) next.city = 'Pasirinkite miestą';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => ({
    title: title.trim(),
    description: description.trim(),
    work_fields: workFields,
    city: city.trim() || null,
    nationwide,
    budget_text: budgetText.trim() || null,
    deadline_text: deadlineText.trim() || null,
  });

  const submit = async (publish: boolean) => {
    setTopError(null);
    if (!validateLocally()) return;
    setIsSaving(true);
    try {
      let gigId: number;
      if (editingId) {
        const { gig } = await freelanceGigsApi.update(parseInt(editingId, 10), buildPayload());
        gigId = gig.id;
      } else {
        const { gig } = await freelanceGigsApi.create(buildPayload());
        gigId = gig.id;
      }
      if (publish) await freelanceGigsApi.publish(gigId);
      navigate(`/freelance/gigs/${gigId}?mode=employer`);
    } catch (err: any) {
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setTopError(err?.message || 'Nepavyko išsaugoti');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleField = (val: string) => {
    setWorkFields((prev) => prev.includes(val)
      ? prev.filter((x) => x !== val)
      : [...prev, val]);
  };

  return (
    <div className="freelance-create-page">
      <h1 className="freelance-create-title">
        {editingId ? 'Redaguoti Freelance Gig' : 'Naujas Freelance Gig'}
      </h1>

      {topError && <div className="freelance-alert freelance-alert--error">{topError}</div>}

      <div className="freelance-form">
        <Field
          label="Kokios paslaugos ieškai? (trumpai)"
          required
          error={errors.title}
        >
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="pvz. Logo dizainui mano start-up'ui"
            maxLength={255}
          />
        </Field>

        <Field
          label="Aprašyk plačiau (min 30 simbolių)"
          required
          error={errors.description}
        >
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Kuo detaliau aprašykite užduotį, būtinus įgūdžius, pageidaujamą stilių ir pan."
          />
          <div className="freelance-field-counter">{description.trim().length} / 30</div>
        </Field>

        <Field
          label="Darbo sritys"
          required
          error={errors.work_fields}
        >
          <div className="freelance-tags">
            {workFields.map((f) => (
              <span key={f} className="freelance-chip">
                {f}
                <button type="button" onClick={() => toggleField(f)} aria-label={`Pašalinti ${f}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="freelance-chip-add"
              onClick={() => setShowWorkFieldsPicker(true)}
            >
              <Plus size={12} /> Pridėti sritį
            </button>
          </div>
        </Field>

        <Field
          label="Pasirink miestą"
          required
          error={errors.city}
        >
          {cityOptions.length > 0 ? (() => {
            const { major, rest } = splitMajorCities(cityOptions);
            return (
              <select value={city} onChange={(e) => setCity(e.target.value)}>
                <option value="">— Pasirinkite —</option>
                {major.length > 0 && (
                  <optgroup label="Didžiausi miestai">
                    {major.map((c) => (
                      <option key={c.id} value={c.value}>{c.value}</option>
                    ))}
                  </optgroup>
                )}
                {rest.length > 0 && (
                  <optgroup label="Kiti miestai">
                    {rest.map((c) => (
                      <option key={c.id} value={c.value}>{c.value}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            );
          })() : (
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="pvz. Vilnius"
            />
          )}
          <div className="freelance-radio-row">
            <label>
              <input
                type="radio"
                name="scope"
                checked={!nationwide}
                onChange={() => setNationwide(false)}
              />
              Tik mieste
            </label>
            <label>
              <input
                type="radio"
                name="scope"
                checked={nationwide}
                onChange={() => setNationwide(true)}
              />
              Visoje Lietuvoje
            </label>
          </div>
        </Field>

        <div className="freelance-field-row">
          <Field label="Terminas (arba data)" hint="laisvas tekstas">
            <input
              type="text"
              value={deadlineText}
              onChange={(e) => setDeadlineText(e.target.value)}
              placeholder="pvz. iki 2026-06-01 arba „2 savaitės"
              maxLength={255}
            />
          </Field>
          <Field label="Biudžetas" hint="jei nežinote — palikite tuščią">
            <input
              type="text"
              value={budgetText}
              onChange={(e) => setBudgetText(e.target.value)}
              placeholder="pvz. 500 EUR / pagal susitarimą"
              maxLength={255}
            />
          </Field>
        </div>

        <div className="freelance-form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => submit(false)}
            disabled={isSaving}
          >
            <Save size={16} /> Išsaugoti juodraštį
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => submit(true)}
            disabled={isSaving}
          >
            <Send size={16} /> Paskelbti ir išsiųsti
          </button>
        </div>
      </div>

      {showWorkFieldsPicker && (
        <div className="freelance-modal-overlay" onClick={() => setShowWorkFieldsPicker(false)}>
          <div className="freelance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="freelance-modal-header">
              <h3>Darbo sritys</h3>
              <button type="button" className="freelance-icon-btn" onClick={() => setShowWorkFieldsPicker(false)}>
                <X size={18} />
              </button>
            </div>
            <input
              type="text"
              className="freelance-modal-search"
              placeholder="Ieškoti..."
              value={workFieldSearch}
              onChange={(e) => setWorkFieldSearch(e.target.value)}
              autoFocus
            />
            <div className="freelance-modal-list">
              {workFieldOptions
                .filter((w) => !workFieldSearch || w.value.toLowerCase().includes(workFieldSearch.toLowerCase()))
                .map((w) => (
                  <label key={w.id} className="freelance-modal-option">
                    <input
                      type="checkbox"
                      checked={workFields.includes(w.value)}
                      onChange={() => toggleField(w.value)}
                    />
                    {w.value}
                  </label>
                ))}
            </div>
            <div className="freelance-modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowWorkFieldsPicker(false)}>
                Gerai
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ label, hint, required, error, children }) => (
  <div className={`freelance-field ${error ? 'has-error' : ''}`}>
    <label className="freelance-field-label">
      {label} {required && <span className="freelance-field-required">*</span>}
      {hint && <span className="freelance-field-hint"> — {hint}</span>}
    </label>
    {children}
    {error && <div className="freelance-field-error">{error}</div>}
  </div>
);

export default FreelanceGigCreate;
